import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, Sparkles } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Spinner } from '@/components/ui/Spinner';
import { aiApi } from '@/features/ai/api';
import { ApiRequestError } from '@/services/httpClient';
import type { AiCommand } from '@/features/ai/types';

const GLOBAL_CONTEXT_ID = 0;

/**
 * The persistent, page-agnostic Chat Assistant — context_type='global'
 * distinguishes this from WorkspaceAiPage's context_type='workspace'
 * conversations, which stay a separate, workspace-scoped thread.
 *
 * This is a fixed command-style helper, not a language model: type or
 * click a known command ("overdue tasks", "team workload", …) and the
 * backend runs a real query against your data. See command_registry.php.
 */
export function AiChatTab() {
  const queryClient = useQueryClient();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<AiCommand[] | null>(null);

  const { data: commands } = useQuery({ queryKey: ['ai', 'commands'], queryFn: aiApi.commands });

  const { data: conversations, isLoading: isLoadingConversations } = useQuery({
    queryKey: ['ai', 'conversations', 'global', GLOBAL_CONTEXT_ID],
    queryFn: () => aiApi.conversations('global', GLOBAL_CONTEXT_ID),
  });
  const conversationId = conversations?.[0]?.id;

  const { data: conversation, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['ai', 'conversations', conversationId],
    queryFn: () => aiApi.getConversation(conversationId!),
    enabled: conversationId !== undefined,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.history?.length]);

  const sendMutation = useMutation({
    mutationFn: async (text: string) => {
      let id = conversationId;
      if (!id) {
        const created = await aiApi.createConversation('global', GLOBAL_CONTEXT_ID, 'Assistant');
        id = created.id;
      }
      return aiApi.sendMessage(id, text);
    },
    onSuccess: (result) => {
      setContent('');
      setError(null);
      setSuggestions(result.suggestions ?? null);
      void queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] });
    },
    onError: (err: unknown) => setError(err instanceof ApiRequestError ? err.message : 'Could not reach the assistant.'),
  });

  function send(text: string) {
    if (!text.trim()) return;
    setSuggestions(null);
    sendMutation.mutate(text.trim());
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        {(isLoadingConversations || isLoadingHistory) && <Spinner label="Loading conversation…" />}
        <div className="flex flex-col gap-3">
          {conversation?.history?.map((entry) => (
            <div key={entry.id} className={`flex ${entry.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${entry.role === 'user' ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'}`}>
                {entry.role === 'assistant' && (
                  <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold opacity-70"><Sparkles className="h-3 w-3" /> Assistant</p>
                )}
                <p className="whitespace-pre-wrap">{entry.role === 'user' ? entry.input_text : entry.output_text}</p>
              </div>
            </div>
          ))}
          {(!conversation || conversation.history?.length === 0) && !isLoadingHistory && (
            <p className="py-8 text-center text-sm text-gray-400">Try a command like "my overdue tasks", "team workload", or "upcoming meetings".</p>
          )}
        </div>
        <div ref={bottomRef} />
      </div>

      {error && <div className="pt-2"><Alert tone="error">{error}</Alert></div>}

      {(suggestions ?? commands) && (suggestions ?? commands)!.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto pt-2 pb-1">
          {(suggestions ?? commands)!.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => send(c.label)}
              className="shrink-0 rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(content); }}
        className="mt-2 flex items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-800"
      >
        <input
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Ask about tasks, projects, meetings…"
          className="flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:disabled:bg-gray-800"
        />
        <button type="submit" disabled={!content.trim() || sendMutation.isPending} className="rounded-xl bg-brand-600 p-2.5 text-white disabled:opacity-40">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
